import { useQueryClient } from "@tanstack/react-query";
import { type MouseEvent, type RefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import {
  CASE_KEY,
  prefetchCase,
  prefetchRecord,
  RECORD_KEY,
  RECORD_NOT_FOUND,
  useCachedRecord,
  useCase,
  useCaseRecord,
  type CaseDetailOut,
  type CaseOut,
  type CaseRecordOut,
  type LastActivityKind,
} from "@/api";
import { Bone } from "@/components/Bone";
import { useCaseHead } from "@/components/CaseHeadSlots";
import { CaseMenu } from "@/components/CaseMenu";
import { useChangeForm } from "@/components/ChangeFormDialog";
import type { MenuItem } from "@/components/Menu";
import { StagePath } from "@/components/stages/StagePath";
import stageStyles from "@/components/stages/Stages.module.css";
import { activityWord } from "@/lib/activity";
import { RESTORE_PARAM } from "@/lib/facadeEditability";
import { CANCEL_PARAM, HANDOVER_CLOSED_REASON, HANDOVER_PARAM, handoverWaitingReason } from "@/lib/caseDialogs";
import { activityTime } from "@/lib/format";
import { setTurnDirection, usePageTurn } from "@/lib/motion";
import { canCancel, HANDOVER_CLOSED, stageOf } from "@/lib/stages";

import { type CaseFacts, type CaseTab, caseTabs, tabOfPage } from "./caseTabs";
import styles from "./CaseFrame.module.css";
import type { PageWidth } from "./pageWidth";

/**
 * Brings the lit tab fully into view in the row, if the row is wider than its
 * box (a phone, or larger text: the tabs are rem-sized). Only the row's own
 * `scrollLeft` moves — `scrollIntoView` would scroll the page as well, and the
 * page must stay where the person put it (as the old rail's `reveal`). The
 * tab is centred, so a neighbour shows on either side and the row reads as
 * one that goes on; at either end the browser stops it at the row's edge.
 * The focus ring is drawn inside the tab (`CaseFrame.module.css`), so the
 * tab's own box is all that has to be in view. Spec §6: „der leuchtende Reiter
 * ist im Bild".
 */
function reveal(row: HTMLElement, tab: Element) {
  if (row.scrollWidth <= row.clientWidth) return;
  const box = row.getBoundingClientRect();
  const link = tab.getBoundingClientRect();
  if (link.left >= box.left && link.right <= box.right) return;
  row.scrollLeft += link.left + link.width / 2 - (box.left + box.right) / 2;
}

/**
 * On mount, and again whenever the lit tab or the set of tabs changes — the
 * answer brings tabs in front of the lit one and moves it. `signature` is
 * that change, spelled out; the effect reads the DOM and not the signature.
 * The lit tab is the one with `aria-current`, `"page"` or `"true"` (R-5).
 */
function useLitTabInView(row: RefObject<HTMLElement | null>, signature: string) {
  useEffect(() => {
    const element = row.current;
    const lit = element?.querySelector("[aria-current]");
    if (element && lit) reveal(element, lit);
  }, [row, signature]);
}

/**
 * The tabs' order is the reading direction (design 2026-10-24 §7): pressing
 * a tab right of the lit one turns the page forward — the old page leaves to
 * the left, the new one comes from the right —, one left of it turns it back.
 * The press says so on `<html>` before the router turns the page
 * (`setTurnDirection`); base.css does the turning. The lit tab itself leads
 * where one is, or to its own page (R-5): no direction, the page rises in.
 * A press that opens the page in another tab or window turns nothing here
 * and says nothing (the router's own test for such a click).
 *
 * The direction stands for the whole turn — the browser plays it after the
 * new page is drawn — and goes once the router says the turn is over
 * (`usePageTurn`), or at once after a turn without a transition (no view
 * transitions, reduced motion): every navigation changes the location's
 * key. Left standing, the next turn from the masthead would slide sideways.
 */
function useTurnDirection(litIndex: number): (event: MouseEvent<HTMLAnchorElement>, index: number) => void {
  const { pathname, key } = useLocation();
  const turning = usePageTurn(pathname);

  useEffect(() => {
    if (!turning) setTurnDirection(null);
  }, [turning, key]);

  return (event, index) => {
    if (event.button !== 0 || event.metaKey || event.altKey || event.ctrlKey || event.shiftKey) return;
    setTurnDirection(litIndex < 0 || index === litIndex ? null : index > litIndex ? "vor" : "zurueck");
  };
}

/** Who holds the Vorgang, what happened last, which Stand — the head's meta line (UX ruling 5, F-13). */
interface Meta {
  owner: string | null;
  /** Only the record's answer knows it; a spoke's detail does not carry it. */
  activity: { at: string; kind: LastActivityKind } | null;
  stand: number;
}

/** What the frame draws from: the rule's three facts, the Vorgang's name, and its meta line. */
interface Known {
  facts: CaseFacts | null;
  name: { caseNo: string; title: string } | null;
  meta: Meta | null;
}

/** The record's `case` and the detail both carry these, in the same words. */
type Named = Pick<CaseOut, "caseNo" | "title" | "status">;

function known(
  stand: number | undefined,
  drawings: number | undefined,
  found: Named | undefined,
  people: Omit<Meta, "stand">,
): Known {
  const status = found?.status;
  return {
    // One of the three missing is as good as all: the rule reads them together.
    facts:
      typeof stand === "number" && typeof drawings === "number" && status !== undefined
        ? { stand, drawings, status }
        : null,
    name: typeof found?.caseNo === "string" ? { caseNo: found.caseNo, title: found.title } : null,
    meta: typeof stand === "number" ? { ...people, stand } : null,
  };
}

function knownFromRecord(data: CaseRecordOut | undefined): Known | null {
  if (data === undefined) return null;
  const at = data.case?.lastActivityAt;
  const kind = data.case?.lastActivityKind;
  return known(data.latestVersionNo, data.documentsCount, data.case, {
    owner: data.ownerDisplayName ?? null,
    activity: typeof at === "string" && kind !== undefined ? { at, kind } : null,
  });
}

function knownFromDetail(data: CaseDetailOut | undefined): Known | null {
  if (data === undefined) return null;
  return known(data.latestVersionNo, data.documents?.length, data, {
    owner: data.owner?.displayName ?? null,
    activity: null,
  });
}

function sameKnown(a: Known, b: Known): boolean {
  return (
    a.facts?.stand === b.facts?.stand &&
    a.facts?.drawings === b.facts?.drawings &&
    a.facts?.status === b.facts?.status &&
    a.name?.caseNo === b.name?.caseNo &&
    a.name?.title === b.name?.title &&
    a.meta?.owner === b.meta?.owner &&
    a.meta?.activity?.at === b.meta?.activity?.at &&
    a.meta?.activity?.kind === b.meta?.activity?.kind &&
    a.meta?.stand === b.meta?.stand
  );
}

/**
 * The meta line under the title (UX ruling 5, F-13; UX gate round 3, #6), as
 * phrases: „Zuständig: Denis Muster · zuletzt geändert heute 14:26
 * (Übergabe) · Stand 4". What happened goes in brackets after the moment; a
 * change the server names no further („bearbeitet") adds nothing to
 * „geändert". A spoke's answer does not know the last activity — its line
 * says who and which Stand, and no moment it would have to guess.
 */
function metaLine(meta: Meta): string {
  const what = meta.activity === null || meta.activity.kind === "other" ? "" : ` (${activityWord(meta.activity.kind)})`;
  return [
    meta.owner !== null ? `Zuständig: ${meta.owner}` : null,
    meta.activity !== null ? `zuletzt geändert ${activityTime(meta.activity.at)}${what}` : null,
    meta.stand > 0 ? `Stand ${meta.stand}` : "noch kein Stand",
  ]
    .filter((part) => part !== null)
    .join(" · ");
}

/**
 * „Mehr …“ on a spoke (Task 9; ruling on B-8): the Vorgang's own gestures,
 * the same ones as on the record. „Form ändern …“ asks its question here —
 * the editor is where it leads anyway; the other three lead to the record
 * with their dialog asked for, as the register's row does (UX gate round 4,
 * #13): one dialog each, in the record (`lib/caseDialogs.ts`). A finished
 * stage holds „Übergeben …“ with its reason, and so does a request that is
 * waiting, as on the record (final review, M-7) — known from what the record
 * last said (`waitingFor`); offered, the record would only drop the dialog
 * asked for. A cancelled Vorgang is offered the way back alone. Not on the outline
 * itself — there the form is being changed already —, and not on the board,
 * whose tool row has the button (UX-Gutachten 2, B-6).
 */
function SpokeMenu({
  caseId,
  facts,
  name,
  here,
  waitingFor,
}: {
  caseId: string;
  facts: CaseFacts;
  name: Known["name"];
  here: CaseTab | undefined;
  /** Whom a waiting request goes to, as the record last said — `null` where none waits, or the record is not known. */
  waitingFor: string | null;
}) {
  const record = `/vorgang/${encodeURIComponent(caseId)}`;
  const change = useChangeForm(caseId, {
    status: facts.status,
    latestVersionNo: here?.key === "umriss" ? 0 : facts.stand,
    caseNo: name?.caseNo ?? "",
    title: name?.title ?? "",
  });
  const items: MenuItem[] =
    facts.status === "cancelled"
      ? [
          {
            key: "restore",
            label: "Wiederherstellen …",
            href: `${record}?${RESTORE_PARAM}=1`,
            internal: true,
          },
        ]
      : [
          // Not on the board: its tool row carries „Form ändern …" beside the
          // walls it changes (UX-Gutachten 2, B-6) — once on a screen.
          ...(change.offered && here?.key !== "planung"
            ? [{ key: "form", label: "Form ändern …", onSelect: change.ask }]
            : []),
          HANDOVER_CLOSED.has(facts.status)
            ? {
                key: "handover",
                label: "Übergeben …",
                disabled: true,
                hint: HANDOVER_CLOSED_REASON,
              }
            : waitingFor !== null
              ? {
                  key: "handover",
                  label: "Übergeben …",
                  disabled: true,
                  hint: handoverWaitingReason(waitingFor),
                }
              : {
                  key: "handover",
                  label: "Übergeben …",
                  href: `${record}?${HANDOVER_PARAM}=1`,
                  internal: true,
                },
          ...(canCancel(facts.status)
            ? [
                {
                  key: "cancel",
                  label: "Vorgang stornieren …",
                  href: `${record}?${CANCEL_PARAM}=1`,
                  internal: true,
                  separatorBefore: true,
                },
              ]
            : []),
        ];
  return (
    <>
      <CaseMenu items={items} />
      {change.dialog}
    </>
  );
}

/** A line's height without its text: the place of a word that is not known yet. */
const NBSP = "\u00a0";

/** How long the pointer rests on a tab before its page is read ahead — as on a register row. */
const REST_MS = 100;

/**
 * Reads a tab's page ahead (design 2026-10-24 §7, as `RegisterRow` does for
 * the record): the pointer resting on it, a press, or the focus coming to it.
 * The record for „Akte“, the detail for every other page — the two answers
 * the frame and the head draw from, so they are there when the page turns.
 */
function useReadAhead(caseId: string): (key: CaseTab["key"]) => {
  onPointerEnter: () => void;
  onPointerLeave: () => void;
  onPointerDown: () => void;
  onFocus: () => void;
} {
  const client = useQueryClient();
  const rest = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(rest.current), []);
  return (key) => {
    const read = () => {
      window.clearTimeout(rest.current);
      if (key === "akte") prefetchRecord(client, caseId);
      else prefetchCase(client, caseId);
    };
    return {
      onPointerEnter: () => {
        window.clearTimeout(rest.current);
        rest.current = window.setTimeout(read, REST_MS);
      },
      onPointerLeave: () => window.clearTimeout(rest.current),
      onPointerDown: read,
      onFocus: read,
    };
  };
}

/**
 * The frame every page of a Vorgang stands in — since Task 9 the Vorgang's
 * one head (UX-Gutachten 1, B-1/B-4; design 2026-10-22 §5, §6).
 *
 * Two rows, the same on every tab. The first: the number over the title —
 * the page's `h1` — and the meta line under it, and at its right the way on
 * and „Mehr …“. The second: the tabs, and the stage path beside them. Before,
 * the record had a head of its own and the spokes a line of capitals over
 * their own title: the Vorgang changed its shape under a tab change, and a
 * page that loaded said so in the middle of it. Now the Vorgang stands and
 * only the page under it turns. The number and the title stand as one piece
 * under the name the list's row flies into (`vorgang-name`, UX-Gutachten 2,
 * B-3), so opening a Vorgang from the list still flies them here, and a tab
 * change keeps them where they are; the head round them is a picture of its
 * own (`vorgang-kopf`) with its tabs' words (`reiter`) and the lit tab's pill
 * (`reiter-marke`). A screen's own title is a section title under it
 * (`PageTitle`).
 *
 * What only the record knows — its way on (which waits for the reading),
 * „Mehr …“ with its dialogs in place, the stage path with its gestures — the
 * record renders itself into the head's places (`InCaseHead`); the frame
 * holds them, with bones while the record loads. On a spoke the frame fills
 * them: „Mehr …“ leading to the record's dialogs, and the path as words.
 *
 * What it reads is what the shell read before it (`useOpenCase`): the record
 * on the record, the case detail on a spoke — one cache entry the screens
 * load anyway, so the frame asks for nothing a screen would not. It never asks
 * for the reading: whether a Vorgang has one is what the *Auslese* tab would
 * need to know, and the drawings stand in for it (`caseTabs.ts`). A tab the
 * pointer rests on reads its page's answer ahead.
 *
 * One frame stays from the record to a spoke and back, and reads the other
 * answer there — which is often not in the cache yet. While it is out, the
 * frame draws from what it already knows of this Vorgang (ruling R-6): the
 * other answer, if the cache still holds it — the page just left read it —,
 * else what it drew last. The bone stands only where it knows nothing yet,
 * the head never says that anything loads, and a fresh answer always wins.
 * Without that, Planung → Akte drew „Akte · ▭" before „Akte · Planung ·
 * Kalkulation", and the tab just pressed moved under the pointer (spec §3,
 * §6).
 *
 * A Vorgang that answers with an error is no open Vorgang — gone, handed
 * over, never mine — and its tabs would lead nowhere: the frame draws
 * nothing and the page says so itself, also while the cache still holds the
 * Vorgang's last answer beside a 404 (final review of Plan D, I-1).
 */
export function CaseFrame({
  caseId,
  onRecord,
  breit,
}: {
  caseId: string;
  onRecord: boolean;
  /** The page's width where it is wider than the content width (`pageWidth`) — the head takes the same. */
  breit?: PageWidth;
}) {
  const { pathname, search } = useLocation();
  const record = useCaseRecord(onRecord ? caseId : null);
  const detail = useCase(onRecord ? null : caseId);
  // On a spoke, what the record last said about a waiting request — read
  // from the cache, never asked for (`SpokeMenu`).
  const cachedRecord = useCachedRecord(onRecord ? null : caseId);
  const query = onRecord ? record : detail;
  const client = useQueryClient();
  const head = useCaseHead();
  const row = useRef<HTMLUListElement>(null);
  const readAhead = useReadAhead(caseId);

  const fresh = onRecord ? knownFromRecord(record.data) : knownFromDetail(detail.data);
  // Read, not watched: the frame waits for its own answer, not for this one.
  const other =
    fresh !== null
      ? null
      : onRecord
        ? knownFromDetail(client.getQueryData<CaseDetailOut>(CASE_KEY(caseId)))
        : knownFromRecord(client.getQueryData<CaseRecordOut>(RECORD_KEY(caseId)));
  // What it drew last, for when the other answer has left the cache too —
  // kept with its Vorgang, so it is never another one's.
  const [seen, setSeen] = useState<{ caseId: string; known: Known } | null>(null);
  const learnt = fresh ?? other;
  if (learnt !== null && (seen === null || seen.caseId !== caseId || !sameKnown(seen.known, learnt))) {
    setSeen({ caseId, known: learnt });
  }
  const { facts, name, meta } =
    learnt ?? (seen?.caseId === caseId ? seen.known : { facts: null, name: null, meta: null });

  const tabs = caseTabs(caseId, facts, tabOfPage(pathname, search), pathname + search);
  useLitTabInView(row, tabs.map((tab) => (tab.current ? `${tab.key}*` : tab.key)).join(" "));
  const turn = useTurnDirection(tabs.findIndex((tab) => tab.current));

  // Gone once the answer says so — a 404 counts even while the cache still
  // holds the Vorgang from a moment ago. Any other failure only where
  // nothing was ever loaded: the page explains it, the frame keeps its tabs.
  // After the hooks: a frame that was drawn and is gone now has the same
  // hooks to run as one that never was.
  const gone = query.isError && (query.data === undefined || query.error?.message === RECORD_NOT_FOUND);
  // Drawn or not, said to the page's title (final review, M-15): without the
  // head there is no `h1` above the page, and its own title takes the part.
  // Before the paint, so the title never shows at the wrong level; back to
  // „shown" when this frame goes, so the next one starts where frames do.
  const setShown = head?.setShown;
  useLayoutEffect(() => {
    setShown?.(!gone);
    return () => setShown?.(true);
  }, [gone, setShown]);
  if (gone) return null;

  // On the record the record fills the head's places once it is shown
  // (`RecordHead`); until then they hold bones in its geometry.
  const recordShown = onRecord && record.data !== undefined && !record.isError;
  const recordWaits = onRecord && record.data === undefined && !record.isError;

  return (
    // A `div`, not a `header`: inside `main` it would be no landmark, and
    // testing-library and older readers take any `header` for the banner.
    <div className={styles.frame} data-breit={breit}>
      <div className={styles.lead}>
        {name !== null ? (
          <div className={styles.identity} data-testid="case-head-identity">
            {/* The Vorgang's name — kicker with its number, and the title —
              as one piece for the page turn (`vorgang-name`, UX-Gutachten 2,
              B-3): opened from the list it grows out of the row's title into
              this head as one picture, never as two groups that arrive apart
              and never as two typefaces over each other. The mesh marker
              before the kicker (design 2026-10-24 §8.6) flies with it — the
              Vorgang's title: the page's one red marker (Task 11,
              UX-Gutachten 1, B-11). */}
            <div className={styles.name}>
              <p className={`label ${styles.kicker}`}>
                <span className="marke marke-titel" aria-hidden="true" />
                Vorgang <span className="num">{name.caseNo}</span>
              </p>
              <h1 ref={head?.setHeading} tabIndex={-1} className={styles.title} title={name.title}>
                {name.title}
              </h1>
            </div>
            {/* One line, cut with an ellipsis where it is narrow (at 375 px
                before „Stand n") — the whole of it in `title` (Task 12). */}
            <p className={styles.meta} title={meta !== null ? metaLine(meta) : undefined}>
              {meta !== null ? metaLine(meta) : NBSP}
            </p>
          </div>
        ) : (
          // The name's place in the real geometry while nothing is known yet:
          // each line in its own face — a non-breaking space — with its bone,
          // never a sentence that something loads (B-4).
          <div className={styles.identity} data-testid="case-head-identity" aria-hidden="true">
            <p className={`label ${styles.kicker} ${styles.boneLine}`}>
              <span className="num">{NBSP}</span>
              <Bone width="7rem" height="0.625rem" />
            </p>
            <p className={`${styles.title} ${styles.boneLine}`}>
              {NBSP}
              <Bone width="18rem" height="1.25rem" />
            </p>
            <p className={`${styles.meta} ${styles.boneLine}`}>
              {NBSP}
              <Bone width="16rem" height="0.75rem" />
            </p>
          </div>
        )}
        <div
          className={styles.actions}
          data-testid={onRecord ? "case-head-actions" : undefined}
          ref={onRecord ? head?.setActions : undefined}
        >
          {onRecord ? (
            recordWaits ? (
              <span className={styles.boneActions} aria-hidden="true">
                <span className={styles.boneButton} style={{ inlineSize: "5.5rem" }} />
                <span className={styles.boneButton} style={{ inlineSize: "9rem" }} />
              </span>
            ) : null
          ) : facts !== null ? (
            <SpokeMenu
              caseId={caseId}
              facts={facts}
              name={name}
              here={tabs.find((tab) => tab.current)}
              waitingFor={cachedRecord.data?.handover?.toDisplayName ?? null}
            />
          ) : (
            <span className={styles.boneActions} aria-hidden="true">
              <span className={styles.boneButton} style={{ inlineSize: "5.5rem" }} />
            </span>
          )}
        </div>
      </div>
      <div className={styles.rail}>
        <nav aria-label="Vorgang" className={styles.nav}>
          <ul ref={row} className={styles.tabs}>
            {tabs.map((tab, index) => (
              <li key={tab.key} className={styles.item}>
                <Link
                  className={styles.tab}
                  to={tab.to}
                  aria-current={tab.ariaCurrent}
                  onClick={(event) => turn(event, index)}
                  {...readAhead(tab.key)}
                >
                  {/* The lit tab's red pill, without a word of its own
                    (UX-Gutachten 2, B-3): it glides to the tab chosen under
                    the words, which stand in the row and are never drawn
                    twice. */}
                  {tab.current ? <span className={styles.pill} aria-hidden="true" /> : null}
                  {tab.label}
                </Link>
              </li>
            ))}
            {facts === null ? (
              // The tabs still to come, in the height of a tab (R4-12): the row
              // does not grow when they arrive.
              <li className={styles.item} aria-hidden="true">
                <Bone width="4.5rem" height="0.875rem" />
              </li>
            ) : null}
          </ul>
        </nav>
        <div
          className={styles.path}
          data-testid={onRecord ? "case-head-path" : undefined}
          ref={onRecord ? head?.setPath : undefined}
        >
          {recordShown ? null : facts !== null && facts.status !== "cancelled" ? (
            // As words, without a gesture: the record's path is the one with
            // „Unterlagen abhaken" and „Angebot raus" in it. A cancelled
            // Vorgang stands nowhere on it — its pages say so.
            <div className={stageStyles.control}>
              <StagePath stage={stageOf(facts.status)} />
            </div>
          ) : facts === null || recordWaits ? (
            <span className={styles.bonePath} aria-hidden="true">
              <Bone width="24rem" height="0.75rem" />
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
}
